<template>
    <div class="container-fluid">
        <h4 v-if="mainHeading" class="py-2">{{ mainHeading }}</h4>

        <div class="row g-4">
            <!-- Available Options Panel -->
            <div class="col-md-6">
                <div class="card h-100">
                    <div class="card-header bg-primary text-white m-0 py-2">
                        <h6 class="card-title d-flex align-items-center mb-0">
                            <i class="material-icons text-lg text-white me-2">format_list_bulleted</i>
                            <span class="text-white">{{ availableOptionsHeading }}</span>
                        </h6>
                    </div>

                    <div class="card-body">
                        <!-- Skeleton Loader -->
                        <template v-if="loading">
                            <div class="skeleton skeleton-input mb-2"></div>
                            <div class="border rounded p-2 mt-2" style="height: 300px;">
                                <div v-for="n in 6" :key="n" class="skeleton skeleton-item mb-2"></div>
                            </div>
                            <div class="mt-3 d-flex justify-content-between align-items-center">
                                <div class="skeleton skeleton-text" style="width: 100px;"></div>
                                <div class="skeleton skeleton-button"></div>
                            </div>
                        </template>

                        <!-- Actual Content -->
                        <template v-else>
                            <!-- Filter Input -->
                            <input id="skillFilter" v-model="skillFilter" type="text" class="form-control"
                                :placeholder="`Search ${optionName}...`" :disabled="props.disabled" />

                            <!-- Available List -->
                            <div class="border rounded p-2 mt-2" style="height: 300px; overflow-y: auto;">
                                <div v-for="skill in filteredAvailableSkills" :key="skill.id" class="form-check mb-2">
                                    <input :id="`skill-${skill.id}`" v-model="selectedSkills" :value="skill"
                                        type="checkbox" class="form-check-input mb-1" :disabled="props.disabled" />
                                    <label :for="`skill-${skill.id}`" class="form-check-label user-select-none"
                                        :style="props.disabled ? 'cursor: not-allowed; opacity: 0.6;' : 'cursor: pointer;'">
                                        {{ skill.name }}
                                    </label>
                                </div>

                                <div v-if="filteredAvailableSkills.length === 0" class="text-center text-muted py-4">
                                    <i class="material-icons text-lg display-4 d-block mb-2">search</i>
                                    No matching {{ optionName || "record" }} found
                                </div>
                            </div>

                            <!-- Actions -->
                            <div class="mt-3 d-flex justify-content-between align-items-center">
                                <small class="text-muted">{{ filteredAvailableSkills.length }} {{ optionName }}
                                    found</small>
                                <button @click="moveSelectedToInterests" :disabled="selectedSkills.length === 0 || props.disabled"
                                    :class="['btn btn-success btn-sm', selectedSkills.length > 0 ? 'pulse-glow' : '']">
                                    <i class="material-icons text-lg me-1">arrow_forward</i>
                                    Add Selected ({{ selectedSkills.length }})
                                </button>
                            </div>
                        </template>
                    </div>
                </div>
            </div>

            <!-- Selected Interests Panel -->
            <div class="col-md-6">
                <div class="card h-100">
                    <div class="card-header bg-success text-white m-0 py-2">
                        <h6 class="card-title d-flex align-items-center mb-0">
                            <i class="material-icons text-lg text-white me-2">favorite</i>
                            <span class="text-white">{{ selectedOptionsHeading }}</span>
                        </h6>
                    </div>

                    <div class="card-body">
                        <!-- Skeleton Loader -->
                        <template v-if="loading">
                            <div class="d-flex justify-content-center gap-2 mb-2">
                                <div class="skeleton skeleton-button-sm"></div>
                                <div class="skeleton skeleton-button-sm"></div>
                            </div>
                            <div class="border rounded p-2 mt-2" style="height: 300px;">
                                <div v-for="n in 5" :key="n" class="skeleton skeleton-item mb-2"></div>
                            </div>
                            <div class="mt-3 d-flex justify-content-between align-items-center">
                                <div class="skeleton skeleton-text" style="width: 100px;"></div>
                                <div class="skeleton skeleton-button"></div>
                            </div>
                        </template>

                        <!-- Actual Content -->
                        <template v-else>
                            <!-- Controls -->
                            <div class="d-flex justify-content-center gap-2">
                                <button @click="moveAllToAvailable" :disabled="userInterests.length === 0 || props.disabled"
                                    class="btn btn-outline-danger btn-sm mb-1" title="Remove all interests">
                                    <i class="material-icons text-lg">keyboard_double_arrow_left</i>
                                </button>
                                <button @click="moveAllToInterests" :disabled="availableSkills.length === 0 || props.disabled"
                                    class="btn btn-outline-success btn-sm mb-1" title="Move all to interests">
                                    <i class="material-icons text-lg">keyboard_double_arrow_right</i>
                                </button>
                            </div>

                            <!-- Selected List -->
                            <div class="border rounded p-2 mt-2" style="height: 300px; overflow-y: auto;">
                                <div v-for="interest in userInterests" :key="interest.id"
                                    class="d-flex justify-content-between align-items-center mb-2 px-2 py-1 bg-light rounded">
                                    <span>{{ interest.name }}</span>
                                    <button @click="removeInterest(interest)"
                                        class="btn btn-outline-danger btn-sm my-0 px-1 py-0" title="Remove"
                                        :disabled="props.disabled">
                                        <i class="material-icons text-sm">close</i>
                                    </button>
                                </div>

                                <div v-if="userInterests.length === 0" class="text-center text-muted py-4">
                                    <i class="material-icons text-lg display-4 d-block mb-2">inbox</i>
                                    No record selected
                                </div>
                            </div>

                            <!-- Save -->
                            <div class="mt-3 d-flex justify-content-between align-items-center">
                                <small class="text-muted">{{ userInterests.length }} {{ optionName }} selected</small>
                                <button @click="saveInterests" :disabled="!hasChanges || isSaving || props.disabled"
                                    :class="['btn btn-success btn-sm', (hasChanges && !isSaving) ? 'pulse-glow' : '']">
                                    <span v-if="isSaving" class="spinner-border spinner-border-sm me-2" role="status"
                                        aria-hidden="true"></span>
                                    <i v-else class="material-icons text-lg me-1">save</i>
                                    {{ isSaving ? 'Saving...' : 'Save' }}
                                </button>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { useToast } from 'vue-toast-notification';

export default {
    name: 'UserInterestsPanel',
    props: {
        mainHeading: { type: String, default: '' },
        availableOptionsHeading: { type: String, default: '' },
        optionName: { type: String, default: '' },
        selectedOptionsHeading: { type: String, default: '' },
        initialSkills: { type: Array, default: () => [] },
        interests: { type: Array, default: () => [] },
        onSave: { type: Function, default: null },
        loading: { type: Boolean, default: false },
        showDefaultMessage: { type: Boolean, default: true },
        disabled: { type: Boolean, default: false },
    },
    emits: ['save-interests', 'update:interests'],
    setup(props, { emit }) {
        const availableSkills = ref([]);
        const userInterests = ref([]);
        const selectedSkills = ref([]);
        const skillFilter = ref('');
        const showSaveSuccess = ref(false);
        const isSaving = ref(false);

        const toast = useToast();

        const baselineIds = ref([]);

        // react to parent updates
        watch(
            () => props.initialSkills,
            (val) => {
                availableSkills.value = [...val];
            },
            { immediate: true, deep: true }
        );

        watch(
            () => props.interests,
            (val) => {
                userInterests.value = [...val];
                if (baselineIds.value.length === 0) {
                    baselineIds.value = val.map((i) => i.id);
                }
            },
            { immediate: true, deep: true }
        );

        const filteredAvailableSkills = computed(() => {
            if (!skillFilter.value) return availableSkills.value;
            return availableSkills.value.filter((skill) =>
                skill.name.toLowerCase().includes(skillFilter.value.toLowerCase())
            );
        });

        const hasChanges = computed(() => {
            const current = userInterests.value.map((i) => i.id).sort();
            const baseline = [...baselineIds.value].sort();
            if (current.length !== baseline.length) return true;
            for (let i = 0; i < current.length; i++) {
                if (current[i] !== baseline[i]) return true;
            }
            return false;
        });

        const moveSelectedToInterests = () => {
            userInterests.value.push(...selectedSkills.value);
            availableSkills.value = availableSkills.value.filter(
                (s) => !selectedSkills.value.some((sel) => sel.id === s.id)
            );
            selectedSkills.value = [];
        };

        const removeInterest = (interest) => {
            availableSkills.value.push(interest);
            userInterests.value = userInterests.value.filter((i) => i.id !== interest.id);
        };

        const moveAllToAvailable = () => {
            availableSkills.value.push(...userInterests.value);
            userInterests.value = [];
        };

        const moveAllToInterests = () => {
            userInterests.value.push(...availableSkills.value);
            availableSkills.value = [];
        };

        const saveInterests = async () => {
            if (isSaving.value) return;
            isSaving.value = true;
            try {
                if (typeof props.onSave === 'function') {
                    await props.onSave(userInterests.value);
                } else {
                    // Fallback to legacy event (cannot await)
                    emit('save-interests', userInterests.value);
                }
                baselineIds.value = userInterests.value.map((i) => i.id);
                emit('update:interests', userInterests.value);

                if (props.showDefaultMessage) {
                    toast.success(`${props.optionName || 'Record'} saved successfully!`, {
                        position: 'top-right'
                    });
                }
            } catch (err) {
                if (props.showDefaultMessage) {
                    toast.error(`Failed to save ${props.optionName || 'record'}.`, {
                        position: 'top-right'
                    });
                }
                console.error('[TagSelector] save failed:', err);
            } finally {
                isSaving.value = false;
            }
        };

        return {
            props,
            availableSkills,
            userInterests,
            selectedSkills,
            skillFilter,
            showSaveSuccess,
            filteredAvailableSkills,
            hasChanges,
            moveSelectedToInterests,
            removeInterest,
            moveAllToAvailable,
            moveAllToInterests,
            saveInterests,
            isSaving
        };
    }
};
</script>

<style scoped>
.card {
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.card-header {
    border-bottom: 1px solid rgba(0, 0, 0, 0.125);
}

.material-icons {
    font-size: 1.25rem;
    vertical-align: middle;
}

.btn-sm .material-icons {
    font-size: 1rem;
}

.toast-container {
    z-index: 1050;
}

/* Skeleton Loader Styles */
.skeleton {
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: loading 1.5s ease-in-out infinite;
    border-radius: 4px;
}

@keyframes loading {
    0% {
        background-position: 200% 0;
    }

    100% {
        background-position: -200% 0;
    }
}

.skeleton-input {
    height: 38px;
    width: 100%;
}

.skeleton-item {
    height: 32px;
    width: 100%;
}

.skeleton-text {
    height: 16px;
}

.skeleton-button {
    height: 31px;
    width: 150px;
}

.skeleton-button-sm {
    height: 31px;
    width: 40px;
}

.pulse-glow {
    animation: pulse-glow 1s infinite;
    box-shadow: 0 0 0 0.2rem #28a74580;
}

@keyframes pulse-glow {
    0% {
        box-shadow: 0 0 0 0.2rem #28a74580;
    }

    50% {
        box-shadow: 0 0 10px 0.3rem #28a745cc;
    }

    100% {
        box-shadow: 0 0 0 0.2rem #28a74580;
    }
}
</style>